/** @format */

import {AreaChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area} from 'recharts';

import {format, parse} from 'date-fns';

import {FILL, GRID_STROKE, STROKE_COLOR_NEW, STROKE_WIDTH} from '../constants/constants';
import CustomSensorsTooltip from './components/CustomSensorsTooltip';
import {getPointKeys} from './helpers/getPointKeys';
import {CustomXTick} from './components/CustomXTick';
import {CustomYTick} from './components/CustomYTick';
import {CustomXTickMonth} from './components/CustomXTickMonth';

export interface AreaChartMultiDataInterface {
  name: string; // 2025-06-01
  month?: string; // июнь
}

export interface TooltipRow {
  label: string;
  value: string;
}

export interface TooltipData {
  dateDisplay: string;
  tooltipRows: TooltipRow[];
}

export type AreaChartRecord = Record<string, number>;

export type AreaChartMultiDataType = AreaChartMultiDataInterface | AreaChartRecord;

export type TooltipDataList = Record<string, TooltipData>;

export interface AreaChartData {
  graphData: AreaChartMultiDataType[];
  tooltipData: TooltipDataList;
}

interface Props {
  data: AreaChartData;
}

const AreaChartMultiData = ({data: {graphData, tooltipData}}: Props) => {
  if (!graphData || !graphData.length) return null;

  const areaKeys = getPointKeys(graphData[0]);
  const monthTicks = graphData.filter(item => item.month);

  return (
    <ResponsiveContainer data-testid='area-chart'>
      <AreaChart
        data={graphData}
        syncId={'areaChartMultiData'}
        margin={{left: 5, top: 5, bottom: -16, right: 16}}
      >
        <CartesianGrid stroke={GRID_STROKE} vertical={false} />

        <XAxis
          tick={props => <CustomXTick {...props} />}
          tickFormatter={recordKey => {
            const date = parse(recordKey, 'yyyy-MM-dd', new Date());
            return format(date, 'dd');
          }}
          dataKey='name'
          interval='preserveStartEnd'
          axisLine={false}
          tickLine={false}
        />

        <XAxis
          xAxisId='month'
          tick={props => <CustomXTickMonth {...props} data={monthTicks} />}
          dataKey='name'
          interval='preserveStartEnd'
          ticks={monthTicks.map(tick => tick.name)}
          axisLine={false}
          tickLine={false}
        />

        <YAxis tick={props => <CustomYTick {...props} />} width={40} allowDecimals={false} />
        <Tooltip
          wrapperStyle={{zIndex: 1000}}
          content={<CustomSensorsTooltip tooltipData={tooltipData} />}
          animationDuration={0}
        />
        {areaKeys.map(key => (
          <Area
            key={key}
            dataKey={key}
            type='bumpX'
            fill={FILL}
            stroke={STROKE_COLOR_NEW}
            strokeWidth={STROKE_WIDTH}
            animationDuration={300}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default AreaChartMultiData;
